/* eslint-disable max-lines */
/* eslint-disable react/no-unknown-property */
/* eslint-disable @scandipwa/scandipwa-guidelines/only-render-in-component */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import ContentWrapper from 'Component/ContentWrapper';
import Html from 'Component/Html';

import { CM_MEASUREMENT, INCHES_MEASUREMENT } from './ProductMeasurements.config';

import './ProductMeasurements.style';

/**
 * Product measurements
 * @class ProductMeasurements
 * @namespace Scandipwa/Component/ProductMeasurements/Component */
export class ProductMeasurementsComponent extends PureComponent {
    static propTypes = {
        measurementsAttributes: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string
        })).isRequired
    };

    /**
     * Default state
     *
     * @type {{cms: boolean}}
     */
    state = {
        cms: true
    };

    cells = null;

    tableWidth = 1;

    onMouseOver = this.onMouseOver.bind(this);

    onButtonClick = this.onButtonClick.bind(this);

    renderMeasurementsTable = this.renderMeasurementsTable.bind(this);

    setCellClassName(cell, className) {
        // eslint-disable-next-line no-param-reassign
        cell.className = className;
    }

    clearCells() {
        if (this.cells) {
            this.cells.forEach((cell) => {
                this.setCellClassName(cell, 'ProductMeasurements-Cell');
            });
        }
    }

    onMouseOver(event) {
        const cell = event.target;
        const table = cell.parentNode.parentNode.parentNode;

        if (!this.cells) {
            this.cells = table.querySelectorAll('th, td');
            this.tableWidth = Array.from(this.cells).filter((cell) => cell.tagName === 'TH').length;
        }

        const cellIndex = Array.prototype.indexOf.call(this.cells, cell);
        const currentCellX = cellIndex % this.tableWidth;
        const currentCellY = Math.trunc(cellIndex / this.tableWidth);

        if (currentCellX && currentCellY) {
            this.cells.forEach((cell, a) => {
                const cellX = a % this.tableWidth;
                const cellY = Math.trunc(a / this.tableWidth);

                if (
                    ((cellY === currentCellY && cellX <= currentCellX)
                    || (cellX === currentCellX && cellY <= currentCellY))
                    && a <= cellIndex
                ) {
                    if (!cellX || !cellY) {
                        this.setCellClassName(cell, 'ProductMeasurements-Cell_edge');
                    } else if (a === cellIndex) {
                        this.setCellClassName(cell, 'ProductMeasurements-Cell_point');
                    } else {
                        this.setCellClassName(cell, 'ProductMeasurements-Cell_intermediate');
                    }
                } else {
                    this.setCellClassName(cell, 'ProductMeasurements-Cell');
                }
            });
        } else {
            this.clearCells();
        }
    }

    onButtonClick(cms) {
        this.cells = null;
        this.setState({
            cms
        });
    }

    /**
     * Renders measurement table
     *
     * @param measurementsAttribute
     * @returns {JSX.Element}
     */
    renderMeasurementsTable(measurementsAttribute, index) {
        const { cms } = this.state;
        const { name, value: html } = measurementsAttribute;

        if (!html) {
            return null;
        }

        const isVisible = (cms && name === CM_MEASUREMENT) || (!cms && name === INCHES_MEASUREMENT);

        return (
            <div
              key={ index }
              block="ProductMeasurements"
              elem="Table"
              mods={ { isVisible } }
              onMouseOver={ this.onMouseOver }
              onFocus={ this.onMouseOver }
              // eslint-disable-next-line react/jsx-no-bind
              onMouseLeave={ () => this.clearCells() }
            >
                <Html content={ html } />
            </div>
        );
    }

    /**
     * Renders measurement tables with switch buttons
     *
     * @returns {JSX.Element}
     */
    renderMeasurementsTables() {
        const { measurementsAttributes } = this.props;

        return (
            <div
              block="ProductMeasurements"
              elem="Tables"
            >
                { this.renderMeasurementsButtons() }
                { measurementsAttributes.map(this.renderMeasurementsTable) }
            </div>
        );
    }

    /**
     * Renders title un subtitle before the measurement tables
     *
     * @returns {JSX.Element}
     */
    renderTitleContent() {
        return (
            <div
              block="ProductMeasurements"
              elem="Header"
            >
                <h2>
                    { __('Product measurements') }
                </h2>
                <p>
                    { __('Find your perfect fit with our detailed product measurement. Important measurements'
                        + ' for each size are listed in the table in both centimeters and inches.') }
                </p>
            </div>
        );
    }

    /**
     * Renders buttons to change measurement
     *
     * @returns {JSX.Element}
     */
    renderMeasurementsButtons() {
        const { cms } = this.state;

        return (
            <div
              block="ProductMeasurements"
              elem="Buttons"
            >
                <button
                  block="ProductMeasurements"
                  elem="Button"
                  mods={ { isActive: !cms } }
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={ () => this.onButtonClick(false) }
                >
                    { __('Inches') }
                </button>
                <button
                  block="ProductMeasurements"
                  elem="Button"
                  mods={ { isActive: cms } }
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={ () => this.onButtonClick(true) }
                >
                    { __('cm') }
                </button>
            </div>
        );
    }

    /**
     * Renders notice text under measurement tables
     *
     * @returns {JSX.Element}
     */
    renderNotice() {
        return (
            <p
              block="ProductMeasurements"
              elem="Notice"
            >
                { __('Please note all measurements shown are specific to this product.') }
            </p>
        );
    }

    /**
     * Renders measurement section content
     *
     * @returns {JSX.Element}
     */
    renderContent() {
        return (
            <>
                { this.renderTitleContent() }
                { this.renderMeasurementsTables() }
                { this.renderNotice() }
            </>
        );
    }

    render() {
        const { measurementsAttributes } = this.props;

        if (!measurementsAttributes.length) {
            return null;
        }

        return (
            <ContentWrapper
              label="Product measurements"
              mix={ { block: 'ProductMeasurements' } }
              wrapperMix={ { block: 'ProductMeasurements', elem: 'Wrapper' } }
            >
                { this.renderContent() }
            </ContentWrapper>
        );
    }
}

export default ProductMeasurementsComponent;
